import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useMemo } from 'react'
import type { ReactNode } from 'react'

import { StatusPill } from '@/shared/components/common'
import { cn } from '@/shared/lib/cn'

import { SortableItem } from './SortableItem'
import type { KanbanColumnSpec, RankedItem, SortableRenderState } from './types'
import { useT } from '@/shared/i18n'

export interface KanbanColumnProps<T extends RankedItem> {
  column: KanbanColumnSpec<T>
  /** Posición de la columna en el tablero. jsdom no hace layout: los tests la usan. */
  columnIndex: number
  renderCard: (item: T, state: SortableRenderState) => ReactNode
  getLabel: (item: T) => string
  className?: string
}

/**
 * Una columna de fase.
 *
 * Es droppable ella misma, y no solo sus tarjetas: sin eso una columna vacía no
 * se puede usar como destino, que es justo el caso de "No comenzado" al
 * empezar la semana.
 */
export function KanbanColumn<T extends RankedItem>({
  column,
  columnIndex,
  renderCard,
  getLabel,
  className,
}: KanbanColumnProps<T>) {
  const t = useT()
  const accepts = column.accepts ?? true
  const { setNodeRef, isOver, active } = useDroppable({ id: column.id, data: { type: 'column' } })
  const ids = useMemo(() => column.items.map((item) => item.id), [column.items])

  const dragging = active !== null
  const highlight = isOver && dragging

  return (
    <section
      aria-label={column.title}
      className={cn('flex w-[260px] shrink-0 flex-col gap-2', className)}
    >
      <header className="flex items-center gap-2">
        <StatusPill category={column.category}>{column.title}</StatusPill>
        <span className="num text-2xs text-text-3">{column.items.length}</span>
        {column.hint ? <span className="ml-auto text-2xs text-text-3">{column.hint}</span> : null}
      </header>

      <div
        ref={setNodeRef}
        role="list"
        aria-label={`${column.title}: tarjetas`}
        data-dnd-column={column.id}
        data-dnd-column-index={columnIndex}
        className={cn(
          'hairline flex min-h-[120px] flex-col gap-2 rounded-[var(--radius-card)] bg-surface-1 p-2',
          'transition-colors duration-[var(--motion-fast)]',
          highlight && accepts && 'ring-1 ring-signal/60',
          highlight && !accepts && 'ring-1 ring-overdue/60',
        )}
      >
        {highlight && !accepts && column.rejectReason ? (
          <p role="tooltip" className="text-2xs text-overdue">
            {column.rejectReason}
          </p>
        ) : null}

        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {column.items.map((item, index) => (
            <SortableItem
              key={item.id}
              id={item.id}
              index={index}
              columnId={column.id}
              columnIndex={columnIndex}
              label={getLabel(item)}
            >
              {(state) => renderCard(item, state)}
            </SortableItem>
          ))}
        </SortableContext>

        {column.items.length === 0 ? (
          <p className="px-1 py-2 text-2xs text-text-3">{t('common.state.nothingHere')}</p>
        ) : null}

        {column.footer}
      </div>
    </section>
  )
}
